import { useMsal } from "@azure/msal-react";
import { useApiClient } from "@fdoh/api-client";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * Hook that wraps API requests with MSAL token acquisition.
 * Ensures a valid access token exists before making API calls.
 *
 * @param applicationName - "graphApi" for Microsoft Graph, anything else for FLEHS API
 */
export function useAuthenticatedApiClient(applicationName: string) {
  // instance: MSAL client for token operations
  // accounts: Array of signed-in accounts (empty = not logged in)
  const { instance, accounts } = useMsal();

  // Base API client from @fdoh/api-client package
  const client = useApiClient(applicationName);

  /**
   * Acquires a token and executes the provided API request.
   * Clears MSAL cache if token cannot be refreshed (forces logout state).
   */
  const getTokenAndMakeRequest = async <T>(
    requestFunction: () => Promise<T>
  ): Promise<T> => {
    // Guard: No accounts means user is not logged in
    if (!accounts || accounts.length === 0) {
      throw new Error("No authenticated user found");
    }

    // Use the first account (typically only one in single-account apps)
    const account = accounts[0];

    // Azure AD requires separate tokens per resource (API)
    // Graph API and FLEHS API are different resources with different scopes
    const scopes =
      applicationName === "graphApi"
        ? ["https://graph.microsoft.com/User.Read"] // Microsoft Graph scope
        : ["api://5b9d1c95-aa2d-4545-af3f-72cc38b875f4/Flehs.User"]; // FLEHS API scope

    try {
      // Attempt silent token acquisition from MSAL cache or via refresh token
      // This succeeds if: valid cached token exists, OR refresh token can get a new one
      // This fails with InteractionRequiredAuthError if: refresh token expired/revoked,
      // or Azure AD policy requires re-authentication (MFA, Conditional Access, etc.)
      await instance.acquireTokenSilent({ scopes, account });

      // Token acquired successfully - make the actual API request
      return await requestFunction();
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Azure AD says user must re-authenticate interactively
        // This happens when: refresh token expired (90 days), password changed,
        // MFA required, Conditional Access policy triggered, or admin revoked tokens

        // Clear MSAL's local cache so the app recognizes user as logged out
        // setActiveAccount(null): Removes the "current user" reference
        // clearCache(): Removes all cached tokens and account data from browser storage
        instance.setActiveAccount(null);
        instance.clearCache();

        // Force full page reload to home - this ensures:
        // 1. All React state is reset
        // 2. MSAL's useMsal hook re-initializes with cleared cache (no accounts)
        // 3. Route guards recognize user as logged out
        window.location.href = "/";

        // Throw error to halt current execution (redirect will handle the rest)
        throw new Error("Session expired. Redirecting to login...");
      }

      // Non-auth errors (network issues, etc.) - re-throw as-is
      throw error;
    }
  };

  return {
    ...client, // Spread base client methods
    authenticatedRequest: getTokenAndMakeRequest, // Add our token-aware wrapper
  };
}
